import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Shield,
  Lock,
  FileSearch,
  UserCheck,
  Globe,
  Database,
  Mail,
  Info,
} from "lucide-react";
import { useTranslation } from "react-i18next";

export default function PrivacyPolicy() {
  const { t } = useTranslation();
  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, delay: i * 0.15 },
    }),
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ======================
              HERO
      ======================= */}
      <header className="relative h-64 md:h-80 lg:h-96 bg-black">
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />

        <div className="relative max-w-5xl mx-auto px-6 py-16 md:py-20">
          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white"
          >
            {t("privacy.hero.title1")}{" "}
            <span className="text-electric-blue">
              {t("privacy.hero.highlight")}
            </span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={1}
            className="mt-3 text-sm md:text-base text-white/80 max-w-2xl"
          >
            {t("privacy.hero.updated")}
            <br />
            {t("privacy.hero.description")}
          </motion.p>
        </div>
      </header>

      {/* ======================
            MAIN CONTENT
      ======================= */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          className="bg-white p-6 rounded-xl shadow-lg mb-10"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={2}
        >
          <div className="flex items-center gap-3 mb-3">
            <FileSearch className="h-5 w-5 text-primary" />
            <h2 className="font-heading text-xl font-bold">
              {t("privacy.summary.title")}
            </h2>
          </div>

          <ul className="font-paragraph space-y-2 text-foreground/80">
            <li>
              <a href="#collect" className="hover:text-primary">
                {t("privacy.summary.collect")}
              </a>
            </li>

            <li>
              <a href="#usage" className="hover:text-primary">
                {t("privacy.summary.usage")}
              </a>
            </li>

            <li>
              <a href="#sharing" className="hover:text-primary">
                {t("privacy.summary.sharing")}
              </a>
            </li>

            <li>
              <a href="#security" className="hover:text-primary">
                {t("privacy.summary.security")}
              </a>
            </li>

            <li>
              <a href="#rights" className="hover:text-primary">
                {t("privacy.summary.rights")}
              </a>
            </li>

            <li>
              <a href="#cookies" className="hover:text-primary">
                {t("privacy.summary.cookies")}
              </a>
            </li>

            <li>
              <a href="#updates" className="hover:text-primary">
                {t("privacy.summary.updates")}
              </a>
            </li>
          </ul>
        </motion.div>

        <motion.div
          className="bg-white rounded-xl shadow-lg p-8 space-y-10"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={3}
        >
          <section id="collect">
            <div className="flex items-center gap-3 mb-3">
              <Database className="h-6 w-6 text-primary" />
              <h2 className="font-heading text-2xl font-semibold">
                {t("privacy.collect.title")}
              </h2>
            </div>

            <p className="font-paragraph text-foreground/80 leading-relaxed">
              {t("privacy.collect.description")}
            </p>

            <ul className="list-disc pl-6 mt-2 space-y-2 text-foreground/80">
              <li>{t("privacy.collect.item1")}</li>
              <li>{t("privacy.collect.item2")}</li>
              <li>{t("privacy.collect.item3")}</li>
              <li>{t("privacy.collect.item4")}</li>
              <li>{t("privacy.collect.item5")}</li>
              <li>{t("privacy.collect.item6")}</li>
              <li>{t("privacy.collect.item7")}</li>
            </ul>
          </section>

          <section id="usage">
            <div className="flex items-center gap-3 mb-3">
              <Info className="h-6 w-6 text-secondary" />
              <h2 className="font-heading text-2xl font-semibold">
                {t("privacy.usage.title")}
              </h2>
            </div>

            <p className="font-paragraph text-foreground/80 leading-relaxed">
              {t("privacy.usage.description")}
            </p>

            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li>{t("privacy.usage.item1")}</li>
              <li>{t("privacy.usage.item2")}</li>
              <li>{t("privacy.usage.item3")}</li>
              <li>{t("privacy.usage.item4")}</li>
              <li>{t("privacy.usage.item5")}</li>
              <li>{t("privacy.usage.item6")}</li>
            </ul>
          </section>

          <section id="sharing">
            <div className="flex items-center gap-3 mb-3">
              <Globe className="h-6 w-6 text-electric-blue" />
              <h2 className="font-heading text-2xl font-semibold">
                {t("privacy.sharing.title")}
              </h2>
            </div>

            <p className="font-paragraph text-foreground/80 leading-relaxed">
              {t("privacy.sharing.description")}
            </p>

            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li>{t("privacy.sharing.item1")}</li>
              <li>{t("privacy.sharing.item2")}</li>
              <li>{t("privacy.sharing.item3")}</li>
              <li>{t("privacy.sharing.item4")}</li>
            </ul>
            <p className="mt-3 text-foreground/80">
              <strong>{t("privacy.sharing.neverSell")}</strong>
            </p>
          </section>

          <section id="security">
            <div className="flex items-center gap-3 mb-3">
              <Lock className="h-6 w-6 text-primary" />
              <h2 className="font-heading text-2xl font-semibold">
                {t("privacy.security.title")}
              </h2>
            </div>

            <p className="font-paragraph text-foreground/80 leading-relaxed">
              {t("privacy.security.description")}
            </p>

            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li>{t("privacy.security.item1")}</li>
              <li>{t("privacy.security.item2")}</li>
              <li>{t("privacy.security.item3")}</li>
              <li>{t("privacy.security.item4")}</li>
            </ul>
          </section>

          <section id="rights">
            <div className="flex items-center gap-3 mb-3">
              <UserCheck className="h-6 w-6 text-secondary" />
              <h2 className="font-heading text-2xl font-semibold">
                {t("privacy.rights.title")}
              </h2>
            </div>

            <p className="font-paragraph text-foreground/80 leading-relaxed">
              {t("privacy.rights.description")}
            </p>

            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li>{t("privacy.rights.item1")}</li>
              <li>{t("privacy.rights.item2")}</li>
              <li>{t("privacy.rights.item3")}</li>
              <li>{t("privacy.rights.item4")}</li>
            </ul>

            <p className="mt-3">
              {t("privacy.rights.contact")}{" "}
              <a
                href="mailto:associationaleoa@gmail.com"
                className="text-primary font-semibold hover:underline"
              >
                aleoaassociation@gmail.com
              </a>
            </p>
          </section>

          <section id="cookies">
            <div className="flex items-center gap-3 mb-3">
              <Shield className="h-6 w-6 text-electric-blue" />
              <h2 className="font-heading text-2xl font-semibold">
                {t("privacy.cookies.title")}
              </h2>
            </div>

            <p className="font-paragraph text-foreground/80 leading-relaxed">
              {t("privacy.cookies.description")}
            </p>

            <p className="mt-3 font-paragraph text-primary hover:underline">
              <Link to="/Cookies">{t("privacy.cookies.link")}</Link>
            </p>
          </section>

          <section id="updates">
            <div className="flex items-center gap-3 mb-3">
              <Mail className="h-6 w-6 text-primary" />
              <h2 className="font-heading text-2xl font-semibold">
                {t("privacy.updates.title")}
              </h2>
            </div>

            <p className="font-paragraph text-foreground/80 leading-relaxed">
              {t("privacy.updates.description")}
            </p>
          </section>
        </motion.div>

        <div className="text-center mt-10">
          <Link
            to="/"
            className="inline-block font-paragraph text-primary hover:underline font-semibold"
          >
            {t("privacy.backHome")}
          </Link>
        </div>
      </main>
    </div>
  );
}
